import { DateTime } from 'luxon';

/** Turning numbers and timestamps into the strings the chat shows. */

/** "2:45 PM", under a bubble. */
export const formatMessageTime = (iso: string) =>
  DateTime.fromISO(iso).toFormat('h:mm a');

/** "Today" / "Yesterday" / "Aug 04, 2026" for the day dividers. */
export const formatDayLabel = (iso: string) => {
  const date = DateTime.fromISO(iso).startOf('day');
  const today = DateTime.now().startOf('day');
  const days = today.diff(date, 'days').days;
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  return date.toFormat('MMM dd, yyyy');
};

/* Built once: the formatter is the expensive part, a lookup is not. Null on
   an engine without `Intl.DisplayNames`, where the code itself is shown. */
const languageNames = (() => {
  try {
    return new Intl.DisplayNames(['en'], { type: 'language' });
  } catch {
    return null;
  }
})();

/** "es" -> "Spanish", for the line under a translated message. */
export const formatLanguageName = (code: string) => {
  try {
    return languageNames?.of(code) ?? code.toUpperCase();
  } catch {
    // `of` throws on a code that is not well formed.
    return code.toUpperCase();
  }
};

/** "3m" / "4h" / "2d" - compact enough for an inbox row's right edge. */
export const formatRelativeTime = (iso: string | null) => {
  if (!iso) return '';
  const diff = DateTime.now().diff(DateTime.fromISO(iso));
  const minutes = Math.floor(diff.as('minutes'));
  if (minutes < 1) return 'now';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(diff.as('hours'));
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(diff.as('days'));
  if (days < 7) return `${days}d`;
  return DateTime.fromISO(iso).toFormat('MMM dd');
};

/**
 * "$24.50", on a triage card.
 *
 * Local rather than `helpers/helpers`' `formatter`, which cannot be reached
 * from here: that module imports `../routes`, which imports the `adminPages`
 * barrel, and closing that cycle renders a blank page with no error at all
 * (see the note at the top of `helpers/appType`).
 *
 * The amount is already in DOLLARS. Order prices elsewhere in the app are
 * cents and go through `centsToDollars` first; a triage card's does not.
 */
const dollars = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

export const formatDollars = (amount: number) => dollars.format(amount);

export const formatBytes = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/** "1:07" - a media player's elapsed / total. */
export const formatDuration = (ms: number) =>
  formatSeconds(Math.round(ms / 1000));

/** The same clock from a whole number of seconds, as the recorder counts them. */
export const formatSeconds = (total: number) =>
  `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
